type AnalysisInfo = {
  symbol: string;
  companyName: string;
};

export const analysis = {
  zh: {
    title: {
      search: "Growin AI 美股分析 | 美股推薦",
      overview: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} 最新股價 & AI 分析 - Growin`,
      value: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} 價值分析｜回測模擬 - Growin`,
      trend: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} 趨勢分析｜回測模擬 - Growin`,
      swing: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} 波段分析｜回測模擬 - Growin`,
      chip: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} 籌碼分析｜大戶持倉 - Growin`,
      dividend: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} 股利分析｜殖利率分析 - Growin`,
      financialReport: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} 財報數據｜圖表 - Growin`,
      stockSentiment: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} 個股情緒分析 - Growin`,
      chipShareHolding: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} 籌碼分析｜內部交易紀錄 - Growin`,
    },
    description: {
      search:
        "Growin AI 美股分析平台，使用獨家量化指標、深入個股分析，視覺化圖表呈現，並提供財報資訊與機構動向，數據導向美股推薦系統，找到理想投資標的！",
      overview: ({ symbol }: { symbol: string }) =>
        `${symbol} 即時股價、AI 個股分析。我們透過 AI 統整財報基本面、技術面、籌碼面等數據，轉化為直觀的五力面向評分，幫助投資人找到強勢潛力股。`,
      value: ({ symbol }: { symbol: string }) =>
        `探索 ${symbol} 的最新財報數據與 AI 驅動的個股評分，深入了解其財務狀況及市場價值，根據 Growin 提供的 AI 分析，助您做出更明智的投資決策。`,
      trend: ({ symbol }: { symbol: string }) =>
        `深入了解 ${symbol} 的技術分析與趨勢數據，查看獨家 AI 技術分析量化數值、均線、ATR、波動率等，並通過 AI 演算法統合的直觀評分，幫助投資者預測市場走勢。`,
      swing: ({ symbol }: { symbol: string }) =>
        `深入了解 ${symbol} 的波段面分析，透過獨家的 AI 量價累積與波段行情分析，評估多頭空頭力道，並根據進出場回測提供有效的波段交易策略，助您做出明智的波段交易決策。`,
      chip: ({ symbol }: { symbol: string }) =>
        `深入分析 ${symbol} 的 13F 大型機構策略佈局、資金流向及內部人持股情況，了解大戶機構的持倉變動與籌碼動向，幫助您掌握市場主力的投資策略。`,
      dividend: ({ symbol }: { symbol: string }) =>
        `深入了解 ${symbol} 的股利面數據，查看殖利率、填息天數、填權息率等關鍵指標，並透過獨家 AI 演算法提供的直觀評分，幫助投資者做出明智的股利投資決策。`,
      financialReport: ({ symbol }: { symbol: string }) =>
        `查看 ${symbol} 的最新財報數據，深入分析其各項財務指標，包括營收、淨收入、營業收入等，全面掌握公司財務狀況，助您做出精準的投資決策。`,
      stockSentiment: ({ symbol }: { symbol: string }) =>
        `探索 ${symbol} 的個股情緒分數與市場聲量，綜合社群輿情與新聞觀點，助您了解投資人對該股的情緒趨勢。`,
      chipShareHolding: ({ symbol }: { symbol: string }) =>
        `深入分析 ${symbol} 的籌碼結構，查看機構持有率、持有機構數量、籌碼分布和內部交易紀錄。透過 Growin 提供的詳細數據，掌握機構投資者的動向與市場趨勢。`,
    },
  },
  en: {
    title: {
      search: "Growin AI Analysis | AI-Powered Stock Analysis for Investor",
      overview: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} Stock Price & AI Analysis - Growin`,
      value: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} Value Analysis | Backtest Simulation - Growin`,
      trend: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} Trend Analysis | Backtest Simulation - Growin`,
      swing: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} Swing Trading Analysis | Backtest Simulation - Growin`,
      chip: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} Stock Whale Analysis | Institutional Holdings - Growin`,
      dividend: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} Dividend Analysis | Yield Analysis - Growin`,
      financialReport: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} Financial Data & Key Indicators - Growin`,
      stockSentiment: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} Stock Sentiment Analysis - Growin`,
      chipShareHolding: ({ symbol, companyName }: AnalysisInfo) =>
        `${symbol} - ${companyName} Stock Whale Analysis & Insider Transactions - Growin`,
    },
    description: {
      search:
        "Growin AI Analysis elevates your investment strategies with 5-Dimensional Analysis, offering profound insights and game-changing market perspectives.",
      overview: ({ symbol }: { symbol: string }) =>
        `Explore the latest stock price and AI-driven analysis for ${symbol}. Access comprehensive data on its financials, stock trends, and AI ratings to make informed investment decisions.`,
      value: ({ symbol }: { symbol: string }) =>
        `Explore the latest financial data and AI-driven stock ratings for ${symbol}. Gain deep insights into its financial health and market value, and make smarter investment decisions with AI analysis provided by Growin.`,
      trend: ({ symbol }: { symbol: string }) =>
        `Dive deep into the technical analysis and trend data for ${symbol}. View exclusive AI-driven technical analysis metrics, including moving averages, ATR, volatility, and more, with intuitive scores generated by AI algorithms to help investors predict market trends.`,
      swing: ({ symbol }: { symbol: string }) =>
        `Gain deep insights into the swing trading analysis of ${symbol}. Through exclusive AI-driven volume-price accumulation and swing market analysis, apply effective swing trading strategies based on entry/exit backtesting to make informed trading decisions.`,
      chip: ({ symbol }: { symbol: string }) =>
        `Dive deep into the 13F institutional strategy layout, capital flow, and insider holdings of ${symbol}. Understand the changes in institutional holdings and chip movement to grasp the investment strategies of market leaders.`,
      dividend: ({ symbol }: { symbol: string }) =>
        `Gain deep insights into the dividend data for ${symbol}, including key metrics such as dividend yield, dividend filling days, and ex-dividend rate. Make smarter dividend investment decisions with intuitive scores provided by our exclusive AI algorithm.`,
      financialReport: ({ symbol }: { symbol: string }) =>
        `Explore the latest financial data for ${symbol}, including key financial metrics such as revenue, net income, and operating income. Gain a comprehensive understanding of the company's financial health to make informed investment decisions.`,
      stockSentiment: ({ symbol }: { symbol: string }) =>
        `Explore sentiment data for ${symbol}, including investor opinion trends and social media analysis. Understand how market sentiment may impact stock performance.`,
      chipShareHolding: ({ symbol }: { symbol: string }) =>
        `Dive deep into the chip structure of ${symbol}, including institutional ownership, number of institutional holders, chip distribution, and insider trading records. Gain insights into institutional investors' movements and market trends with detailed data provided by Growin.`,
    },
  },
};
